/**
 * In-memory per-unit token cache (FE-1684, 2b).
 *
 * Caches unit-login session data so returning to a previously-visited home in
 * the same session reuses its token instead of a fresh `/login`. Module-level,
 * so it's dropped on reload/logout and is only ever an optimization: callers
 * must always be able to fall back to a real login.
 */
import type { UserData } from 'src/types/UserData';

export type CachedUnitSession = Partial<UserData>;

const cache = new Map<string, CachedUnitSession>();

/**
 * Composite cache key scoped by account so one account can never reuse another
 * account's unit token. Owned homes have an empty sub segment.
 */
const keyFor = (email: string, unitId?: string, subId?: string): string =>
	`${String(email ?? '').toLowerCase()}|${unitId ?? ''}|${subId ?? ''}`;

export const getCachedUnitSession = (
	email: string,
	unitId?: string,
	subId?: string,
): CachedUnitSession | undefined => cache.get(keyFor(email, unitId, subId));

export const setCachedUnitSession = (
	email: string,
	unitId: string | undefined,
	subId: string | undefined,
	data: CachedUnitSession | undefined,
): void => {
	// Nothing worth caching without a token to reuse.
	if (!data?.token) return;
	cache.set(keyFor(email, unitId, subId), data);
};

/** Remove every cached unit session for a single account. */
export const invalidateAccountSessions = (email: string): void => {
	const prefix = `${String(email ?? '').toLowerCase()}|`;
	for (const key of cache.keys()) {
		if (key.startsWith(prefix)) cache.delete(key);
	}
};

/** Drop the entire cache (logout-all / force logout). */
export const clearUnitTokenCache = (): void => {
	cache.clear();
};
